// ─────────────────────────────────────────────────────────────────────────────
// Excel de «Brillamos Juntos» — las 7 hojas del libro descargable, con la forma visual de la
// plantilla del cliente `02 - Campana brillamos juntos.xlsx` (auditoría 2026-09-05, medida celda a
// celda con openpyxl): banner teal de título + subtítulo, bloque de metadatos celeste
// (Periodo / Filtros aplicados / Generado), encabezado de tabla teal con «Ajustar texto», rejilla
// de bordes finos y anchos de columna suficientes para que ninguna celda salga apretada.
//
// Aquí viven solo las hojas PROPIAS de este reporte —«Resumen Ejecutivo», «Definiciones» y
// «Distribución Fundamental»—: el sistema visual (banner, columnas, metadatos, rejilla) y las tres
// hojas que este libro comparte con «Reconocimiento Diario» viven en `excelPlantillaTeal.ts`, que
// las dos plantillas del cliente calcan con el mismo diseño. Se re-exportan desde aquí para que
// `ShineTab.tsx` siga importando su libro completo de un solo módulo.
//
// EL LIBRO COMPLETO SALE EN EL IDIOMA DE QUIEN DESCARGA (`reports.shine.*`), en los 7 idiomas —
// mismo criterio ya aplicado en Líderes, Demográfico e Históricos. Un archivo que se comparte fuera
// de la app tiene que poder leerse sin saber español. Los rótulos de dato se reusan de las claves
// que la PANTALLA ya usa (`recognitionsMade`, `adoptionStaff`, `colName`…) para que la celda del
// Excel y la columna de la tabla digan lo mismo; solo los textos exclusivos del archivo viven bajo
// `reports.shine.excel.*`.
//
// Vive fuera de `ShineTab.tsx` para que el componente se quede con la descarga y no con la
// especificación de estilos, igual que `historicosExcel.ts`/`leadersExcel.ts` frente a sus tabs.
//
// Diferencias A PROPÓSITO frente a la plantilla de referencia:
//   1. Sin las tres filas en blanco que la plantilla reserva arriba del banner (`A1:A3` combinadas,
//      el hueco de su logotipo): este libro no inserta logotipo, y el hueco quedaría como una franja
//      vacía sin explicación.
//   2. Sin los pies «Datos ficticios demo generados para validar diseño y fórmulas» ni «Datos de
//      ejemplo del entorno demo»: describen el archivo de MUESTRA del cliente, no un reporte con
//      datos reales (mismo criterio ya aplicado en Líderes, Históricos y Moderación).
//   3. «Usuarios totales» y «Sin actividad» se explican como dato DE LA CAMPAÑA. La plantilla los
//      redacta como un histórico de toda la vida del usuario («alguna vez», «en su historia»), que
//      no es lo que el backend calcula (`CampaignReportDto.adopcionTotal`, acotado a la campaña):
//      copiar esa redacción metería un dato falso en el archivo.
//   4. «Líderes» usa el mismo rótulo en «Resumen Ejecutivo» y en «Definiciones». La plantilla dice
//      «…donde participa líder» en una hoja y «…entre líderes» en la otra para el mismo KPI, que es
//      una inconsistencia de un archivo hecho a mano, no una intención de diseño.
//   5. Los KPI «Personas únicas reconocidas», «Personas únicas que reconocen» y «Tasa de
//      participación» no están en la plantilla — los pide la spec de Reportería. Van al FINAL de la
//      tabla para que las nueve filas que la plantilla sí trae salgan exactamente en su orden.
// ─────────────────────────────────────────────────────────────────────────────
import { mergeDeFila } from '@/utils/exportExcelMultiSheet';
import type { ExcelCellStyleHint, ExcelCellValue, ExcelSheetSpec } from '@/utils/exportExcelMultiSheet';
import { excelColors } from '@/utils/excelTheme';
import { renderBarChartPng } from '@/utils/chartToImage';
import type { RankedEntryDto } from '@/api/recognition';
import {
  CENTRADO_VERTICAL, FORMATO_ENTERO, FORMATO_PORCENTAJE, FORMATO_TASA, FILA_HEADER_TABLA_SIMPLE,
  TEMA_PLANTILLA_TEAL, columnaDeTexto, columnaNumerica, estilosDeBanner, estilosDeMetadatos,
  rejillaDeTabla,
} from './excelPlantillaTeal';
import type { FilaDefinicionGlosario, Traductor } from './excelPlantillaTeal';

// Re-exportados para que `ShineTab.tsx` importe el libro entero de un solo módulo: son las piezas
// que este reporte comparte, sin cambios, con «Reconocimiento Diario» (ver `excelPlantillaTeal.ts`).
export {
  SIN_DATO, buildEmbudoSheet, buildHojaSinDatos, buildRankingSheet,
  buildTodosLosReconocimientosSheet,
} from './excelPlantillaTeal';
export type { Traductor } from './excelPlantillaTeal';
/** Alias histórico de `FilaRankingPlantilla`: el nombre con el que `ShineTab.tsx` ya la importaba. */
export type { FilaRankingPlantilla as FilaRankingBrillamos } from './excelPlantillaTeal';


/** Columnas del Resumen Ejecutivo: KPI · Valor · Detalle, más la cuarta del bloque de plantilla. */
const TOTAL_COLUMNAS_RESUMEN = 4;
/** Hasta dónde llega el banner y el bloque de metadatos, tal como los extiende la plantilla. */
const ANCHO_BANNER_RESUMEN = 6;
const TOTAL_COLUMNAS_DEFINICIONES = 3;
const TOTAL_COLUMNAS_DISTRIBUCION = 3;

/** Anchos medidos de la plantilla (columnas A-D de «Resumen Ejecutivo»). */
const ANCHOS_RESUMEN = [45.66, 18.11, 43.89, 18.11];
/** Anchos medidos de la plantilla (columnas A-C de «Definiciones»). */
const ANCHOS_DEFINICIONES = [40, 47.55, 53.33];
/** Anchos medidos de la plantilla (columnas A-C de «Distribución Fundamental»). */
const ANCHOS_DISTRIBUCION = [48.56, 15.33, 21];

/** Filas 0-indexed del bloque «Periodo / Filtros aplicados / Generado» del Resumen Ejecutivo. */
const FILAS_META_RESUMEN = [3, 4, 5];
/** Fila 0-indexed del encabezado «KPI | Valor | Detalle» (banner + 1 en blanco + los 3 metadatos
 *  + 1 en blanco). */
const FILA_HEADER_RESUMEN = 7;
/** Fila 0-indexed del encabezado «Dato | Qué es | Cómo se calcula» (banner + 2 en blanco). */
const FILA_HEADER_DEFINICIONES = 4;

/**
 * KPIs del Resumen Ejecutivo, EN EL ORDEN EN QUE SE PINTAN. Los conteos viajan como NÚMERO (o como
 * el guion de «sin dato» cuando el agregado está suprimido o el censo no respondió), nunca como
 * texto ya formateado: un `"77.9%"` string se alineaba a la izquierda mientras los conteos
 * numéricos se iban a la derecha, y la columna «Valor» salía con los datos en dos alineaciones
 * distintas — el defecto que este rediseño corrige. Así, además, el número sigue siendo sumable y
 * ordenable en Excel.
 *
 * Los porcentajes viajan como FRACCIÓN 0-1, no como el número ya multiplicado por cien: el formato
 * nativo (`0.00%`) hace la conversión al pintar.
 */
export interface KpisBrillamos {
  pares: ExcelCellValue;
  lideres: ExcelCellValue;
  adopcionStaff: ExcelCellValue;
  detalleAdopcionStaff: string;
  adopcionFrontline: ExcelCellValue;
  detalleAdopcionFrontline: string;
  usuariosTotales: ExcelCellValue;
  detalleUsuariosTotales: string;
  sinActividad: ExcelCellValue;
  reconocimientosEnviados: ExcelCellValue;
  totalIndividual: ExcelCellValue;
  totalGrupal: ExcelCellValue;
  nominadosUnicos: ExcelCellValue;
  participantes: ExcelCellValue;
  tasaParticipacion: ExcelCellValue;
  plantillaTotal: ExcelCellValue;
  plantillaStaff: ExcelCellValue;
  plantillaFrontline: ExcelCellValue;
}

/** Metadatos del banner: los tres renglones celestes que la plantilla pone sobre la tabla de KPIs. */
export interface MetadatosBrillamos {
  nombreDeCampana: string;
  periodo: string;
  filtrosAplicados: string;
  generado: string;
}

/**
 * Hoja «Resumen Ejecutivo». El orden de los KPI es el de la plantilla y NO debe reordenarse: Pares,
 * Líderes, Usuarios Staff, Usuarios Frontline, Usuarios totales, Sin actividad, Total de
 * reconocimientos enviados, Total a una sola persona y Total a grupos. Los tres KPI que la spec
 * agrega y la plantilla no trae cierran la tabla (nota 5 del encabezado del archivo), y el bloque
 * «Plantilla | Total | Staff | Frontline» la remata igual que en la plantilla.
 */
export function buildResumenEjecutivoSheet(
  traducir: Traductor, metadatos: MetadatosBrillamos, kpis: KpisBrillamos,
): ExcelSheetSpec {
  const banner = estilosDeBanner(ANCHO_BANNER_RESUMEN);

  const filasKpi: Array<{ label: string; valor: ExcelCellValue; detalle: string; formato: string }> = [
    {
      label: traducir('excel.kpiPeers'),
      valor: kpis.pares,
      detalle: traducir('excel.whatPeers'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('excel.kpiLeaders'),
      valor: kpis.lideres,
      detalle: traducir('excel.whatLeaders'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('adoptionStaff'),
      valor: kpis.adopcionStaff,
      detalle: kpis.detalleAdopcionStaff,
      formato: FORMATO_PORCENTAJE,
    },
    {
      label: traducir('adoptionFrontline'),
      valor: kpis.adopcionFrontline,
      detalle: kpis.detalleAdopcionFrontline,
      formato: FORMATO_PORCENTAJE,
    },
    {
      label: traducir('adoptionTotal'),
      valor: kpis.usuariosTotales,
      detalle: kpis.detalleUsuariosTotales,
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('noActivity'),
      valor: kpis.sinActividad,
      detalle: traducir('excel.whatNoActivity'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('excel.kpiTotalSent'),
      valor: kpis.reconocimientosEnviados,
      detalle: traducir('excel.whatRecognitionsMade'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('excel.kpiIndividual'),
      valor: kpis.totalIndividual,
      detalle: traducir('excel.whatIndividual'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('excel.kpiGroup'),
      valor: kpis.totalGrupal,
      detalle: traducir('excel.whatTeamRecognitions'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('uniqueRecognized'),
      valor: kpis.nominadosUnicos,
      detalle: traducir('excel.whatUniqueRecognized'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('uniqueRecognizers'),
      valor: kpis.participantes,
      detalle: traducir('excel.whatUniqueRecognizers'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('participationRate'),
      valor: kpis.tasaParticipacion,
      detalle: traducir('excel.whatParticipationRate'),
      formato: FORMATO_TASA,
    },
  ];

  const primeraFilaKpi = FILA_HEADER_RESUMEN + 1;
  const ultimaFilaKpi = primeraFilaKpi + filasKpi.length - 1;
  const filaHeaderPlantilla = ultimaFilaKpi + 2;
  const filaPlantilla = filaHeaderPlantilla + 1;

  const rows: ExcelCellValue[][] = [
    [traducir('excel.summaryTitle')],
    [metadatos.nombreDeCampana],
    [],
    [traducir('excel.metaPeriod'), metadatos.periodo],
    [traducir('excel.metaFilters'), metadatos.filtrosAplicados],
    [traducir('excel.metaGenerated'), metadatos.generado],
    [],
    [traducir('excel.colKpi'), traducir('excel.colValue'), traducir('excel.colDetail')],
    ...filasKpi.map((kpi): ExcelCellValue[] => [kpi.label, kpi.valor, kpi.detalle]),
    [],
    [
      traducir('excel.headcountTitle'), traducir('excel.headcountTotal'),
      traducir('excel.headcountStaff'), traducir('excel.headcountFrontline'),
    ],
    [
      traducir('excel.headcountPeople'), kpis.plantillaTotal, kpis.plantillaStaff, kpis.plantillaFrontline,
    ],
  ];

  return {
    name: traducir('excel.sheetSummary'),
    rows,
    styleHints: {
      theme: TEMA_PLANTILLA_TEAL,
      headerRows: [FILA_HEADER_RESUMEN, filaHeaderPlantilla],
      columnWidths: ANCHOS_RESUMEN,
      rowHeights: banner.rowHeights,
      merges: [
        ...banner.merges,
        ...FILAS_META_RESUMEN.map((fila) => mergeDeFila(fila, 1, ANCHO_BANNER_RESUMEN - 1)),
      ],
      dataColumnStyles: {
        startRow: primeraFilaKpi,
        endRow: ultimaFilaKpi,
        columns: [
          { ...columnaDeTexto(0), fillArgb: excelColors.filaPar },
          {
            column: 1,
            fillArgb: excelColors.filaPar,
            fontColorArgb: excelColors.valorDestacado,
            isBold: true,
            // La alineación se fija para TODA la columna, no por celda: mezcla números y el guion
            // de «sin dato», y sin esto Excel manda el número a la derecha y el guion a la
            // izquierda — la columna «Valor» salía con los datos en dos alineaciones distintas.
            horizontal: 'right',
            vertical: CENTRADO_VERTICAL,
          },
          {
            ...columnaDeTexto(2),
            fillArgb: excelColors.filaPar,
            fontColorArgb: excelColors.textoDetalle,
          },
        ],
      },
      cellStyles: [
        ...banner.cellStyles,
        ...estilosDeMetadatos(FILAS_META_RESUMEN),
        // Un formato numérico por FILA: la columna «Valor» mezcla conteos, porcentajes de adopción
        // y la tasa de participación, así que no puede fijarse a nivel de columna.
        ...filasKpi.map((kpi, indice): ExcelCellStyleHint => (
          { row: primeraFilaKpi + indice, column: 1, numFmt: kpi.formato }
        )),
        // Bloque «Plantilla»: la etiqueta a la izquierda y las tres cifras alineadas a la derecha,
        // igual que la columna «Valor» de arriba.
        { row: filaPlantilla, column: 0, horizontal: 'left', vertical: CENTRADO_VERTICAL },
        ...[1, 2, 3].map((columna): ExcelCellStyleHint => ({
          row: filaPlantilla,
          column: columna,
          fontColorArgb: excelColors.valorDestacado,
          isBold: true,
          horizontal: 'right',
          vertical: CENTRADO_VERTICAL,
          numFmt: FORMATO_ENTERO,
        })),
      ],
      borderedRanges: [
        rejillaDeTabla(FILAS_META_RESUMEN[0], FILAS_META_RESUMEN.at(-1)!, ANCHO_BANNER_RESUMEN - 1),
        rejillaDeTabla(FILA_HEADER_RESUMEN, ultimaFilaKpi, TOTAL_COLUMNAS_RESUMEN - 2),
        rejillaDeTabla(filaHeaderPlantilla, filaPlantilla, TOTAL_COLUMNAS_RESUMEN - 1),
      ],
    },
  };
}

/**
 * Las once definiciones que trae la plantilla, EN SU ORDEN, más las cinco que documentan las hojas
 * de detalle que este libro sí exporta y la plantilla no explicaba. Función de módulo para que
 * `buildDefinicionesSheet` se quede solo con el armado de la hoja.
 */
function definicionesDeLaPlantilla(traducir: Traductor): FilaDefinicionGlosario[] {
  return [
    {
      dato: traducir('recognitionsMade'),
      queEs: traducir('excel.whatRecognitionsMade'),
      comoSeCalcula: traducir('excel.calcRecognitionsMade'),
    },
    {
      dato: traducir('uniqueRecognized'),
      queEs: traducir('excel.whatUniqueRecognized'),
      comoSeCalcula: traducir('excel.calcUniqueRecognized'),
    },
    {
      dato: traducir('excel.datumTeamRecognitions'),
      queEs: traducir('excel.whatTeamRecognitions'),
      comoSeCalcula: traducir('excel.calcTeamRecognitions'),
    },
    {
      dato: traducir('excel.kpiIndividual'),
      queEs: traducir('excel.whatIndividual'),
      comoSeCalcula: traducir('excel.calcIndividual'),
    },
    {
      dato: traducir('excel.kpiPeers'),
      queEs: traducir('excel.whatPeers'),
      comoSeCalcula: traducir('excel.calcPeers'),
    },
    {
      dato: traducir('excel.kpiLeaders'),
      queEs: traducir('excel.whatLeaders'),
      comoSeCalcula: traducir('excel.calcLeaders'),
    },
    {
      dato: traducir('adoptionStaff'),
      queEs: traducir('excel.whatStaffAdoption'),
      comoSeCalcula: traducir('excel.calcStaffAdoption'),
    },
    {
      dato: traducir('adoptionFrontline'),
      queEs: traducir('excel.whatFrontlineAdoption'),
      comoSeCalcula: traducir('excel.calcFrontlineAdoption'),
    },
    {
      dato: traducir('adoptionTotal'),
      queEs: traducir('excel.whatTotalUsers'),
      comoSeCalcula: traducir('excel.calcTotalUsers'),
    },
    {
      dato: traducir('noActivity'),
      queEs: traducir('excel.whatNoActivity'),
      comoSeCalcula: traducir('excel.calcNoActivity'),
    },
    // Las dos filas de audiencia de la plantilla explican QUÉ es cada segmento; no tienen columna
    // «Cómo se calcula» porque no son un cálculo, sino la definición del padrón.
    {
      dato: traducir('excel.datumAudienceStaff'),
      queEs: traducir('excel.whatAudienceStaff'),
      comoSeCalcula: '',
    },
    {
      dato: traducir('excel.datumAudienceFrontline'),
      queEs: traducir('excel.whatAudienceFrontline'),
      comoSeCalcula: '',
    },
    {
      dato: traducir('participationRate'),
      queEs: traducir('excel.whatParticipationRate'),
      comoSeCalcula: traducir('excel.calcParticipationRate'),
    },
    {
      dato: traducir('uniqueRecognizers'),
      queEs: traducir('excel.whatUniqueRecognizers'),
      comoSeCalcula: traducir('excel.calcUniqueRecognizers'),
    },
    {
      dato: traducir('excel.sheetDistribution'),
      queEs: traducir('excel.whatDistribution'),
      comoSeCalcula: traducir('excel.calcDistribution'),
    },
    {
      dato: traducir('excel.sheetFunnel'),
      queEs: traducir('excel.whatFunnel'),
      comoSeCalcula: traducir('excel.calcFunnel'),
    },
    {
      dato: traducir('excel.sheetAllRecognitions'),
      queEs: traducir('excel.whatAllRecognitions'),
      comoSeCalcula: traducir('excel.calcAllRecognitions'),
    },
  ];
}

/**
 * Hoja «Definiciones» — el glosario «Dato · Qué es · Cómo se calcula» de la plantilla, con sus once
 * filas en el mismo orden, las cinco que este libro agrega para las hojas de detalle que sí exporta
 * y el recuadro crema que aclara cuándo un rol cuenta como líder.
 *
 * `isTruncado` agrega una fila más al final: si el recorrido de «Todos los reconocimientos» se cortó
 * en el tope duro de 5000 filas, el archivo es INCOMPLETO y hay que decirlo. Un archivo recortado en
 * silencio parece completo, y las decisiones que se toman con él salen mal sin que nadie sepa por qué.
 */
export function buildDefinicionesSheet(traducir: Traductor, isTruncado: boolean): ExcelSheetSpec {
  const banner = estilosDeBanner(TOTAL_COLUMNAS_DEFINICIONES);
  const definiciones = definicionesDeLaPlantilla(traducir);
  if (isTruncado) {
    definiciones.push({
      dato: traducir('excel.datumTruncated'),
      queEs: traducir('excel.whatTruncated'),
      comoSeCalcula: traducir('excel.calcTruncated'),
    });
  }

  const primeraFilaDato = FILA_HEADER_DEFINICIONES + 1;
  const ultimaFilaDato = primeraFilaDato + definiciones.length - 1;
  const filaNotaLider = ultimaFilaDato + 2;

  const rows: ExcelCellValue[][] = [
    [traducir('excel.definitionsTitle')],
    [traducir('excel.definitionsSubtitle')],
    [],
    [],
    [traducir('excel.colDatum'), traducir('excel.colWhatIs'), traducir('excel.colHowCalculated')],
    ...definiciones.map((definicion): ExcelCellValue[] => (
      [definicion.dato, definicion.queEs, definicion.comoSeCalcula]
    )),
    [],
    [traducir('excel.datumLeader'), traducir('excel.noteLeader'), ''],
  ];

  return {
    name: traducir('excel.sheetDefinitions'),
    rows,
    styleHints: {
      theme: TEMA_PLANTILLA_TEAL,
      headerRows: [FILA_HEADER_DEFINICIONES],
      columnWidths: ANCHOS_DEFINICIONES,
      rowHeights: banner.rowHeights,
      merges: banner.merges,
      dataColumnStyles: {
        startRow: primeraFilaDato,
        endRow: ultimaFilaDato,
        // Sin `fillArgb`: el banding zebra blanco/celeste del tema ya pinta estas filas, igual que
        // en la plantilla, y fijar un color aquí lo borraría.
        columns: [0, 1, 2].map((columna) => ({ ...columnaDeTexto(columna), vertical: 'top' as const })),
      },
      cellStyles: [
        ...banner.cellStyles,
        // Recuadro crema del pie: la plantilla lo usa para la aclaración de qué cuenta como líder.
        ...[0, 1, 2].map((columna): ExcelCellStyleHint => ({
          row: filaNotaLider,
          column: columna,
          fillArgb: excelColors.notaFondo,
          fontColorArgb: excelColors.notaTexto,
          isBold: columna === 0,
          hasWrapText: true,
          horizontal: 'left',
          vertical: CENTRADO_VERTICAL,
        })),
      ],
      borderedRanges: [
        rejillaDeTabla(FILA_HEADER_DEFINICIONES, ultimaFilaDato, TOTAL_COLUMNAS_DEFINICIONES - 1),
        rejillaDeTabla(filaNotaLider, filaNotaLider, TOTAL_COLUMNAS_DEFINICIONES - 1),
      ],
    },
  };
}

/**
 * Un reconocimiento puede llevar MÁS de un Fundamental y/o MÁS de un Hábito a la vez (REQ-008,
 * `multiFundamental`), y el backend cuenta cada uno de los que tenga. Consecuencia visible: la suma
 * de los Fundamentales puede superar el total de la campaña, y la suma de los Hábitos de un
 * Fundamental puede superar el conteo de ESE Fundamental. Sin la nota que dispara esta función, esa
 * suma «que no cuadra» se lee como un error de cálculo en vez de un dato real sobre la campaña.
 */
export function haySumaQueNoCoincide(
  topFundamentales: RankedEntryDto[],
  rankingHabitosTodos: RankedEntryDto[],
  padreDelHabito: Map<string, string>,
  total: number,
): boolean {
  const sumaFundamentales = topFundamentales.reduce((acumulado, fundamental) => acumulado + fundamental.count, 0);
  if (sumaFundamentales !== total) return true;

  return topFundamentales.some((fundamental) => {
    const sumaHijos = rankingHabitosTodos
      .filter((habito) => padreDelHabito.get(habito.id) === fundamental.id)
      .reduce((acumulado, habito) => acumulado + habito.count, 0);
    return sumaHijos > 0 && sumaHijos !== fundamental.count;
  });
}

/**
 * Hoja «Distribución Fundamental»: jerarquía de 2 niveles (Fundamental → sus Hábitos, indentados con
 * tres espacios, el mismo prefijo visual de la plantilla) + gráfica de barras horizontales
 * rasterizada — `exceljs` no escribe gráficas nativas de Excel, ver `chartToImage.ts`.
 *
 * Las filas de Fundamental van con el verde agua de la plantilla y en negrita; las de Hábito
 * conservan el banding zebra. El «% del total» es NÚMERO con formato nativo (`0.00%`), no el texto
 * `"12.34%"` que salía antes: un texto no se ordena, no se grafica y se alinea del lado contrario
 * al de los conteos de su misma tabla.
 */
export function buildDistribucionFundamentalSheet(opciones: {
  traducir: Traductor;
  topFundamentales: RankedEntryDto[];
  total: number;
  nombreDeFundamental: (id: string) => string;
  colorDeFundamental: (id: string) => string;
  rankingHabitosTodos: RankedEntryDto[];
  padreDelHabito: Map<string, string>;
  nombreDeHabito: (id: string) => string;
}): ExcelSheetSpec {
  const {
    traducir, topFundamentales, total, nombreDeFundamental, colorDeFundamental, rankingHabitosTodos,
    padreDelHabito, nombreDeHabito,
  } = opciones;

  const banner = estilosDeBanner(TOTAL_COLUMNAS_DISTRIBUCION);
  const nota: ExcelCellValue[][] = haySumaQueNoCoincide(
    topFundamentales, rankingHabitosTodos, padreDelHabito, total,
  ) ? [[traducir('excel.distributionNote')]] : [];

  const filas: ExcelCellValue[][] = [];
  /** Índices (relativos a la primera fila de datos) de las filas de Fundamental, no de Hábito. */
  const filasDeFundamental: number[] = [];
  for (const fundamental of topFundamentales) {
    filasDeFundamental.push(filas.length);
    filas.push([
      nombreDeFundamental(fundamental.id),
      total > 0 ? fundamental.count / total : 0,
      fundamental.count,
    ]);

    const hijos = rankingHabitosTodos.filter((habito) => padreDelHabito.get(habito.id) === fundamental.id);
    for (const habito of hijos) {
      filas.push([
        `   ${nombreDeHabito(habito.id)}`,
        fundamental.count > 0 ? habito.count / fundamental.count : 0,
        habito.count,
      ]);
    }
  }

  const filaHeader = FILA_HEADER_TABLA_SIMPLE + nota.length;
  const primeraFilaDato = filaHeader + 1;
  const ultimaFilaDato = primeraFilaDato + filas.length - 1;
  const FILAS_RESPIRO_ANTES_DE_IMAGEN = 2;
  const grafica = renderBarChartPng(
    traducir('excel.distributionChart'),
    topFundamentales.map((fundamental) => ({
      etiqueta: nombreDeFundamental(fundamental.id),
      valor: fundamental.count,
      color: colorDeFundamental(fundamental.id),
    })),
  );

  return {
    name: traducir('excel.sheetDistribution'),
    rows: [
      [traducir('excel.distributionTitle')],
      [traducir('excel.distributionSubtitle')],
      ...nota,
      [],
      [traducir('excel.colFundamentals'), traducir('excel.colPercentOfTotal'), traducir('colRecognitions')],
      ...filas,
    ],
    styleHints: {
      theme: TEMA_PLANTILLA_TEAL,
      headerRows: [filaHeader],
      columnWidths: ANCHOS_DISTRIBUCION,
      rowHeights: banner.rowHeights,
      merges: [
        ...banner.merges,
        ...nota.map((_, indice) => mergeDeFila(2 + indice, 0, TOTAL_COLUMNAS_DISTRIBUCION - 1)),
      ],
      dataColumnStyles: {
        startRow: primeraFilaDato,
        endRow: ultimaFilaDato,
        columns: [
          columnaDeTexto(0),
          columnaNumerica(1, FORMATO_PORCENTAJE),
          columnaNumerica(2, FORMATO_ENTERO),
        ],
      },
      cellStyles: [
        ...banner.cellStyles,
        ...nota.map((_, indice): ExcelCellStyleHint => ({
          row: 2 + indice,
          column: 0,
          fillArgb: excelColors.notaFondo,
          fontColorArgb: excelColors.notaTexto,
          hasWrapText: true,
          vertical: CENTRADO_VERTICAL,
        })),
        // Nivel padre: verde agua y negrita, para que la jerarquía se lea de un vistazo aunque la
        // indentación de los hábitos se pierda al copiar y pegar la tabla.
        ...filasDeFundamental.flatMap((desplazamiento) => [0, 1, 2].map((columna): ExcelCellStyleHint => ({
          row: primeraFilaDato + desplazamiento,
          column: columna,
          fillArgb: excelColors.grupoFondo,
          isBold: true,
        }))),
      ],
      borderedRanges: [rejillaDeTabla(filaHeader, ultimaFilaDato, TOTAL_COLUMNAS_DISTRIBUCION - 1)],
    },
    image: {
      base64: grafica.base64,
      tl: { col: 0, row: ultimaFilaDato + FILAS_RESPIRO_ANTES_DE_IMAGEN },
      ext: { width: grafica.width, height: grafica.height },
    },
  };
}
